import { ObjectId } from '@racemap/sdk/schema/base';
import {
  type EnrichedTracker,
  EnrichedTrackerSchema,
  type TrackerPrototype,
} from '@racemap/sdk/schema/trackers';
import { HTTPFetchError, RacemapAPIClient } from '@racemap/utilities/api-client';
import { deepUpdateRecord } from '@racemap/utilities/functions/deepUpdateRecord';
import type { TrackerLegacy } from '@racemap/utilities/types/trackers';
import { castDraft, type Immutable, produce } from 'immer';
import { Tristate } from 'src/components/utils/tristateTask';
import { toast } from 'src/lib/toast';

import type { StoreApi } from 'zustand';
import type { DraftState, State } from '../../reducers';
import { validateTrackerPrototypes } from '../trackers_helper';
import type { TrackersActions } from '../trackers_reducers';
import { refreshTagCounts } from './tags';

const apiClient = RacemapAPIClient.fromWindowLocation();

export const createTrackerActions = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): Pick<
  TrackersActions,
  'updateTracker' | 'updateTrackers' | 'removeTrackers' | 'addTrackersToEvent' | 'addTrackers'
> => ({
  updateTracker: async (trackerId, trackerUpdate) => {
    const oldTracker = get().trackers.items.get(trackerId.toHexString());
    if (oldTracker == null) return;

    const newTracker = EnrichedTrackerSchema.parse({
      ...oldTracker,
      ...trackerUpdate,
    });

    try {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.SPINNING;
          s.trackers.items.set(trackerId.toHexString(), newTracker);
        }),
      );

      const [result] = await apiClient.updateTrackers([trackerId.toHexString()], trackerUpdate);
      if (result == null) throw new Error('Failed to update tracker. Empty response.');
      const updatedTracker = EnrichedTrackerSchema.parse(result);

      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.NORMAL;
          s.trackers.items.set(trackerId.toHexString(), updatedTracker);
          toast.success('Tracker updated successfully.');
        }),
      );
    } catch (error) {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.FAILURE;
          s.trackers.items.set(trackerId.toHexString(), castDraft(oldTracker));
        }),
      );
      if (error instanceof Error) {
        console.error('Failed to update a tracker');
        console.error(error);
        toast.error('The attempt to update the tracker failed.');
      }
    }
  },
  updateTrackers: async (trackerIds, trackerUpdates) => {
    const oldTrackers = trackerIds.map((trackerId) =>
      get().trackers.items.get(trackerId.toHexString()),
    );

    if (oldTrackers.includes(undefined)) return;
    try {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.SPINNING;

          trackerIds.forEach((trackerId) => {
            const oldTracker = s.trackers.items.get(trackerId.toHexString());
            if (oldTracker) {
              const mergedTracker = deepUpdateRecord(oldTracker, trackerUpdates);
              const newTracker = EnrichedTrackerSchema.parse(mergedTracker);
              s.trackers.items.set(trackerId.toHexString(), newTracker);
            }
          });
        }),
      );

      const stringTrackerIds = trackerIds.map((id) => id.toHexString());
      const results = await apiClient.updateTrackers(stringTrackerIds, trackerUpdates);

      set(
        produce((s: DraftState) => {
          results.forEach((result) => {
            const updatedTracker = EnrichedTrackerSchema.parse(result);
            s.trackers.items.set(String(result.id), updatedTracker);
          });
          s.trackers.savingState = Tristate.NORMAL;
          toast.success(`${trackerIds.length} tracker(s) updated successfully.`);
        }),
      );
    } catch (error) {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.FAILURE;
          trackerIds.forEach((trackerId, index) => {
            const oldTracker = oldTrackers[index];
            if (oldTracker) {
              s.trackers.items.set(trackerId.toHexString(), castDraft(oldTracker));
            }
          });
        }),
      );

      console.error('Failed to update trackers:', error);
      toast.error('The attempt to update the trackers failed.');
    }
  },
  removeTrackers: async (trackerIds) => {
    const removedTrackers: Array<Immutable<EnrichedTracker>> = [];
    const user = get().users.getter.currentUser();
    const items = get().trackers.items;

    try {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.SPINNING;
          for (const trackerId of trackerIds) {
            const trackerToRemove = items.get(trackerId.toHexString());

            if (trackerToRemove != null) {
              if (user != null && !user.admin && trackerToRemove.owner.toHexString() !== user.id)
                continue;
              removedTrackers.push(trackerToRemove);
              s.trackers.items.delete(trackerId.toHexString());
            }
          }
          refreshTagCounts(s);
        }),
      );

      const trackerIdsToRemove = removedTrackers.map((t) => t.id.toHexString());
      await apiClient.deleteTrackers(trackerIdsToRemove);

      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.NORMAL;
        }),
      );
    } catch (error) {
      console.log(error);
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.FAILURE;

          for (const tracker of removedTrackers) {
            s.trackers.items.set(tracker.id.toHexString(), castDraft(tracker));
          }
          refreshTagCounts(s);
        }),
      );
      if (error instanceof Error) {
        console.log(`Failed to remove a tracker: ${error.message}`);
        console.error(error);
        toast.error('The attempt to remove the tracker failed.');
      }
    }
  },
  addTrackersToEvent: async (trackerIds, eventId, pairingOption) => {
    try {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.SPINNING;
        }),
      );

      const connections = await apiClient.connectDevicesWithStarter(
        trackerIds.map((t) => t.toHexString()),
        eventId,
        {
          pairingOption,
        },
      );

      set(
        produce((s: DraftState) => {
          for (const trackerId of Object.values(connections)) {
            const tracker = s.trackers.items.get(trackerId);
            if (tracker == null) continue;

            if (!tracker.eventIds?.includes(new ObjectId(eventId))) {
              tracker.eventIds?.push(new ObjectId(eventId));
            }
          }
          s.trackers.savingState = Tristate.NORMAL;
        }),
      );

      return { success: true, count: Object.keys(connections).length };
    } catch (err) {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.FAILURE;
        }),
      );
      if (err instanceof HTTPFetchError) {
        console.error(err);
        toast.error(`Failed to add trackers to event! ${err.serverError}`);
      } else if (err instanceof Error) {
        console.error(err);
        toast.error(`Failed to add trackers to event! ${err.message}`);
      }
      return { success: false, count: -1 };
    }
  },
  addTrackers: async (trackersToAdd): Promise<Array<EnrichedTracker> | undefined> => {
    try {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.SPINNING;
        }),
      );
      validateTrackerPrototypes(trackersToAdd);
      const preparedTracker = prepareTrackerPrototypes(trackersToAdd);
      const createdTrackersRaw = await apiClient.addTrackers(preparedTracker);
      const createdTrackers = createdTrackersRaw.map((t) => EnrichedTrackerSchema.parse(t));

      let addedCount = 0;
      let updatedCount = 0;

      set(
        produce((s: DraftState) => {
          for (const tracker of createdTrackers) {
            const trackerId = tracker.id.toHexString();
            if (s.trackers.items.has(trackerId)) {
              updatedCount++;
            } else {
              addedCount++;
            }
            s.trackers.items.set(trackerId, tracker);
          }
          s.trackers.savingState = Tristate.NORMAL;
        }),
      );

      const pluralizeTracker = (count: number) =>
        `${count} ${count === 1 ? 'tracker' : 'trackers'}`;

      let message = '';
      if (addedCount && updatedCount) {
        message = `Added ${pluralizeTracker(addedCount)} and updated ${pluralizeTracker(updatedCount)}.`;
      } else if (addedCount) {
        message = `Added ${pluralizeTracker(addedCount)}.`;
      } else if (updatedCount) {
        message = `Updated ${pluralizeTracker(updatedCount)}.`;
      } else {
        message = 'No changes were made to the trackers.';
      }

      toast.info(message);
      return createdTrackers;
    } catch (error) {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.FAILURE;
        }),
      );
      if (error instanceof HTTPFetchError) {
        console.log(`Failed to add a tracker: ${error.message}`);
        toast.error(
          `The attempt to add the tracker failed. Error: ${error.serverError || error.message}`,
        );
      } else if (error instanceof Error) {
        console.log(`Failed to add a tracker: ${error.message}`);
        toast.error(`The attempt to add the tracker failed. Error: ${error.message}`);
      }
    }
  },
});

const prepareTrackerPrototypes = (
  prototypes: Array<TrackerPrototype>,
): Array<
  Partial<TrackerLegacy> & { trackerId: string; trackerType: string; trackerName: string }
> => {
  return prototypes.map((prototype) => ({
    id: prototype.id?.toHexString(),
    trackerId: prototype.trackerId,
    trackerType: prototype.trackerType,
    trackerName: prototype.trackerName,
    meta: {
      iccid: prototype.iccid,
    },
  }));
};
